/**
 * Report config types — the data model for the Twin Report Generator.
 *
 * A "report" is a dashboard with three additions:
 *   - **pagination** — `pages[]` instead of a single grid
 *   - **narrative blocks** — title / executive summary / per-section
 *     commentary / conclusion text
 *   - **report-level filters** — date range, line, shift, parts (filters
 *     cascade to every page's widgets)
 *
 * Reuses the existing `WidgetInstance` shape from `lib/dashboard/types`
 * for renderable sections. Pack-shipped templates carry `source: "pack"`
 * (read-only); user-created reports are `source: "my"`.
 *
 * See docs/backlog/b2.x-report-generator.md § C3 for the design rationale.
 * Phase-1 swaps localStorage persistence for `/v1/twin/reports` CRUD.
 *
 * Export pipeline (PDF / XLSX / CSV / image bundle) lands in B2.1+.
 * B2.0 ships save-only + render-as-scrolling-page preview.
 */

import type { WidgetInstance } from "@/lib/dashboard/types";

/** Audience source — pack-shipped templates are read-only. */
export type ReportSource = "my" | "pack" | "shared";

/**
 * One renderable section inside a page. Mutually-exclusive shape:
 *   - `widget` → render via DashboardRenderer's widget catalog
 *   - `narrative` → render markdown-style text block
 *
 * Splitting widget vs narrative at the section level (not page level)
 * lets users interleave a Pareto chart with a paragraph of explanation
 * naturally — exactly the shape an imported plant's analysis report
 * already takes.
 */
export type ReportSection =
  | {
      id: string;
      kind: "widget";
      widget: WidgetInstance;
    }
  | {
      id: string;
      kind: "narrative";
      /** Block heading (optional). */
      heading?: string;
      /** Markdown-ish body text. B2.0 renders as plain text + paragraphs. */
      body: string;
    };

/** One page of the report. PDF export (B2.1) breaks pages here. */
export interface ReportPage {
  id: string;
  /** Page header (rendered at top of the page). */
  title?: string;
  /** Page-level subheading shown under the title. */
  subtitle?: string;
  sections: ReportSection[];
}

/** Narrative blocks that wrap the whole report (not page-scoped). */
export interface ReportNarrative {
  /** Cover-page title; defaults to the report's name. */
  title?: string;
  /** Cover-page subtitle (date range, tenant name, etc.). */
  subtitle?: string;
  /** Executive summary — top of page 1, before any widget content. */
  executiveSummary?: string;
  /** Closing summary / next steps — bottom of last page. */
  conclusion?: string;
}

/** Report-level filters — cascade to all widget data sources. */
export interface ReportFilters {
  /** ISO date — start of the reporting window. */
  dateFrom?: string;
  /** ISO date — end of the reporting window. */
  dateTo?: string;
  /** Line ids to include. Empty = all lines. */
  lines?: string[];
  /** Shift ids to include. Empty = all shifts. */
  shifts?: string[];
  /** Part numbers to include. Empty = all parts. */
  parts?: string[];
}

/** Complete report config. */
export interface ReportConfig {
  id: string;
  name: string;
  description?: string;
  /** Pack id when source === "pack". */
  pack?: string;
  /** If this report was spawned from a template, the template id. */
  templateId?: string;
  source: ReportSource;
  /** Owner display name. */
  owner: string;
  /** ISO timestamps. */
  createdAt: string;
  updatedAt: string;
  /** Page-scoped content. */
  pages: ReportPage[];
  /** Cover-page + closing narrative. */
  narrative: ReportNarrative;
  /** Filters that apply to every widget in every page. */
  filters: ReportFilters;
}

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────

/** Read-only check — pack-shipped reports cannot be edited in place. */
export function isReportEditable(r: Pick<ReportConfig, "source">): boolean {
  return r.source !== "pack";
}

/** Count widgets across all pages — used in gallery thumbnails. */
export function countReportWidgets(r: ReportConfig): number {
  let count = 0;
  for (const p of r.pages) {
    for (const s of p.sections) {
      if (s.kind === "widget") count++;
    }
  }
  return count;
}

/** True when a widget of the report reads an imported dataset (a saved report built on an import). */
export function reportReadsImport(r: Pick<ReportConfig, "pages">): boolean {
  return r.pages.some((p) =>
    p.sections.some((s) => s.kind === "widget" && !!s.widget.dataSource?.importedDatasetId),
  );
}

/**
 * The report's range as its meta line says it, or null to say none. An import's report takes its window from the
 * days its rows carry, and has none when they carry no dates: the line says that window is
 * open, where it used to show the template's sample window (an April). A window with one bound says so. A report with
 * no window that reads no import says nothing, as before: its widgets read their own.
 */
export function reportRangeText(r: Pick<ReportConfig, "filters" | "pages">): string | null {
  const { dateFrom, dateTo } = r.filters;
  if (dateFrom && dateTo) return `${dateFrom} → ${dateTo}`;
  if (dateFrom) return `from ${dateFrom}, no end set`;
  if (dateTo) return `up to ${dateTo}, no start set`;
  return reportReadsImport(r) ? "open, the imported rows carry no dates" : null;
}

/** Count narrative blocks across all pages + global. */
export function countReportNarratives(r: ReportConfig): number {
  let count = 0;
  if (r.narrative.executiveSummary?.trim()) count++;
  if (r.narrative.conclusion?.trim()) count++;
  for (const p of r.pages) {
    for (const s of p.sections) {
      if (s.kind === "narrative") count++;
    }
  }
  return count;
}
